import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import CalenderIcon from "../../../../../assets/images/searchbars/calendar.png";
import DateRangePicker from "../../../../Common/DateRangePicker/DateRangePicker";
import "../../../../Common/DateRangePicker/DateRangePicker.scss";
// Legacy (kept for reference, not in use): import Calendar from "../../SubComponents/Calendar/calendar";
import "../../HotelSearchBar/hotelsearchbar.scss";

const WIDTH_PHONE = 768;
const WIDTH_TABLET = 1024;

const getLayoutVariant = (width) => {
  if (width <= WIDTH_PHONE) return "phone";
  if (width <= WIDTH_TABLET) return "tablet";
  return "desktop";
};

const DatesInput = ({
  calendarDateArrival,
  setCalendarDateArrival,
  calendarDateLeave,
  setCalendarDateLeave,
  possibleArrivalDates,
  possibleDepartureDates,
  pricesByDate = null, // Object mapping date strings (DD/MM/YYYY) to cheapest prices
  ldsDates = null, // optional: passed to picker so on phone return dates are computed from draft arrival
  /** "RoundTrip" | "OneWay" — hotels/packages omit or leave default RoundTrip */
  tripType = "RoundTrip",
  /** טיסות RT: תאריך חזרה לא מוגבל לרשימת LDS */
  unrestrictedReturnDates = false,
  destinationCode = "",
  /** "default" | "agent" */
  appearance = "default",
  enableMonthMode = false,
  dateMode = "exact",
  onDateModeChange = null,
  selectedMonth = "",
  onMonthSelect = null,
  availableMonths = null,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [layoutVariant, setLayoutVariant] = useState(() =>
    typeof window !== "undefined" ? getLayoutVariant(window.innerWidth) : "desktop"
  );
  const bodyScrollLockRef = useRef(null);
  const { t } = useTranslation();
  const ref = useRef(null);
  const pickerRef = useRef(null);

  const isPhone = layoutVariant === "phone";
  const isTablet = layoutVariant === "tablet";

  useEffect(() => {
    const updateLayout = () => {
      const w = typeof window !== "undefined" ? window.innerWidth : 1025;
      setLayoutVariant(getLayoutVariant(w));
    };
    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  // Body scroll lock on phone when picker is open
  useEffect(() => {
    if (!isPhone || !isCalendarOpen) return;
    bodyScrollLockRef.current = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      if (bodyScrollLockRef.current) {
        document.body.style.overflow = bodyScrollLockRef.current.overflow ?? "";
        document.body.style.position = bodyScrollLockRef.current.position ?? "";
        document.body.style.width = bodyScrollLockRef.current.width ?? "";
        bodyScrollLockRef.current = null;
      }
    };
  }, [isPhone, isCalendarOpen]);

  const toggleCalendar = () => setIsCalendarOpen((open) => !open);

  const handleClosePicker = () => setIsCalendarOpen(false);

  // Click outside to close on tablet and desktop only
  useEffect(() => {
    if (isPhone || !isCalendarOpen) return;
    const listener = (event) => {
      const clickedInsideInput = ref.current?.contains(event.target);
      const clickedInsidePicker = pickerRef.current?.contains(event.target);
      if (!clickedInsideInput && !clickedInsidePicker) setIsCalendarOpen(false);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [isPhone, isCalendarOpen]);

  const variant = isPhone ? "phone" : isTablet ? "tablet" : "desktop";
  const isMonthMode = enableMonthMode && dateMode === "month";

  const formatMonthLabel = (monthKey) => {
    if (!monthKey) return "";
    const parsed = dayjs(monthKey, "MM/YYYY");
    if (!parsed.isValid()) return monthKey;
    const lang =
      typeof localStorage !== "undefined" && localStorage.getItem("i18nextLng")
        ? localStorage.getItem("i18nextLng")
        : "he";
    return new Intl.DateTimeFormat(lang, {
      month: "long",
      year: "numeric",
    }).format(parsed.toDate());
  };

  /** Display only: show date range as earlier → later (min - max). Does not change state. */
  const getDisplayDateRange = () => {
    if (tripType === "OneWay") {
      return calendarDateArrival?.length ? calendarDateArrival : null;
    }
    if (!calendarDateArrival?.length || !calendarDateLeave?.length) return null;
    const parse = (d) => {
      const parts = String(d).trim().split("/");
      if (parts.length !== 3) return 0;
      const [dd, mm, yyyy] = parts.map(Number);
      return (yyyy || 0) * 10000 + (mm || 0) * 100 + (dd || 0);
    };
    const a = parse(calendarDateArrival);
    const b = parse(calendarDateLeave);
    if (a <= b) return `${calendarDateArrival} - ${calendarDateLeave}`;
    return `${calendarDateLeave} - ${calendarDateArrival}`;
  };

  const pickerComponent = isCalendarOpen ? (
    <>
      {isPhone && (
        <div
          className="drp-portal-backdrop"
          onClick={handleClosePicker}
          aria-hidden="true"
        />
      )}
      <div
        ref={pickerRef}
        className={`drp-portal ${isPhone ? "drp-portal-phone" : isTablet ? "drp-portal-tablet" : "drp-portal-desktop"}`}
      >
        <DateRangePicker
          variant={variant}
          calendarDateArrival={calendarDateArrival}
          calendarDateLeave={calendarDateLeave}
          saveArrivalDate={setCalendarDateArrival}
          saveDepartureDate={setCalendarDateLeave}
          possibleArrivalDates={possibleArrivalDates}
          possibleDepartureDates={possibleDepartureDates}
          pricesByDate={pricesByDate}
          onClose={handleClosePicker}
          ldsDates={ldsDates}
          tripType={tripType}
          unrestrictedReturnDates={unrestrictedReturnDates}
          destinationCode={destinationCode}
          appearance={appearance}
          enableMonthMode={enableMonthMode}
          dateMode={dateMode}
          onDateModeChange={onDateModeChange}
          selectedMonth={selectedMonth}
          onMonthSelect={onMonthSelect}
          availableMonths={availableMonths}
        />
      </div>
    </>
  ) : null;

  return (
    <div
      className="hotelSearchBar-item cursor-pointer hotelSearchBar-item-calender tw-flex tw-flex-col"
      ref={ref}
    >
      <div
        className="hotelSearchBar-desc-wrapper cursor-pointer"
        onClick={toggleCalendar}
      >
        <div className="hotelSearchBar-icon-wrapper">
          <img
            className="hotelSearchBar-icon2"
            src={CalenderIcon}
            alt=""
          ></img>
        </div>

        <div className="hotelSearchBar-label-wrapper cursor-pointer">
          {appearance === "agent" ? (
            isMonthMode && selectedMonth ? (
              <div className="agent-dates-summary" aria-label="חודש נבחר">
                <div className="agent-dates-summary__item agent-dates-summary__item--month">
                  <span className="agent-dates-summary__label">חודש</span>
                  <span className="agent-dates-summary__value">
                    {formatMonthLabel(selectedMonth)}
                  </span>
                </div>
              </div>
            ) : calendarDateArrival || calendarDateLeave ? (
              <div className="agent-dates-summary" aria-label="תאריכים נבחרים">
                <div className="agent-dates-summary__item agent-dates-summary__item--departure">
                  <span className="agent-dates-summary__label">יציאה</span>
                  <span className="agent-dates-summary__value">
                    {calendarDateArrival || "__/__/____"}
                  </span>
                </div>
                {tripType !== "OneWay" ? (
                  <div className="agent-dates-summary__item agent-dates-summary__item--return">
                    <span className="agent-dates-summary__label">חזרה</span>
                    <span className="agent-dates-summary__value">
                      {calendarDateLeave || "__/__/____"}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <label className="hotelSearchBar-label1 cursor-pointer">תאריכים</label>
                <label className="hotelSearchBar-label2 hotelSearchBar-letter-spacing cursor-pointer">
                  בחר תאריכי יציאה וחזרה
                </label>
              </>
            )
          ) : (
            <>
              <label className="hotelSearchBar-label1 cursor-pointer">
                {isMonthMode && selectedMonth
                  ? "חודש"
                  : tripType === "OneWay"
                    ? t("flightsearch.dateLabelOneWay")
                    : t("hotelsearchbar.selectDate")}
              </label>

              {(() => {
                if (isMonthMode && selectedMonth) {
                  return (
                    <label className="hotelSearchBar-label2 cursor-pointer">
                      {formatMonthLabel(selectedMonth)}
                    </label>
                  );
                }
                const displayRange = getDisplayDateRange();
                return displayRange ? (
                  <label className="hotelSearchBar-label2 cursor-pointer">
                    {displayRange}
                  </label>
                ) : (
                  <label className="hotelSearchBar-label2 hotelSearchBar-letter-spacing cursor-pointer">
                    {tripType === "OneWay"
                      ? t("flightsearch.datePlaceholderOneWay")
                      : t("hotelsearchbar.date")}
                  </label>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {/* Calendar - Render in portal on both mobile and desktop to avoid stacking context issues */}
      {typeof document !== "undefined" ? (
        createPortal(pickerComponent, document.body)
      ) : (
        pickerComponent
      )}

      <input
        defaultValue={calendarDateLeave}
        className="hotelSearchBar-calendar-required"
        required={tripType !== "OneWay"}
        aria-hidden="true"
        tabIndex={-1}
      ></input>
    </div>
  );
};

export default DatesInput;
